/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

/**
 * Autoplay preference.
 *
 * Mirrors GH's AutoplayModal setting (`queueStore.autoplayEnabled`):
 * when ON, playback keeps going with machine-picked similar tracks after
 * the picked queue ends. GH persists this locally with no GraphQL
 * mutation; we do the same — the flag is read at end-of-queue in
 * {@link @/playback/autoplayContinuation}, which re-seeds a fresh
 * continuation queue (matching GH's `_handlePlayerQueueEnded`).
 *
 * Defaults ON, matching GH's `queueStore.autoplayEnabled` (`?? true`);
 * the HeaderMenu Autoplay row lets a user turn it off.
 */

import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { createPersistentItem, globalRef } from '@amazon/vinyl-util'
import { boolean } from '@amazon/vinyl-validation'
import { ctx } from '@/util/context'
import { globalData } from '@/util/globalData'

/** Persistence key for the autoplay toggle. `true` = autoplay on. */
export const AUTOPLAY_ENABLED_KEY = 'autoplayEnabled'

const { errorHandler } = ctx('autoplay')

// Validated boolean persistence — a corrupt / legacy stored value is
// rejected and falls back to `true` (default-on) instead of being trusted.
const autoplayEnabled = createPersistentItem<boolean>({
    key: AUTOPLAY_ENABLED_KEY,
    defaultValue: true,
    validator: boolean().assert,
})

// Persistence is async; seed ON and hydrate once resolved, unless a write
// landed first — the stored value it would restore is older than that write.
const state = globalRef(() => {
    const current = { enabled$: data(true), written: false }
    autoplayEnabled
        .get()
        .then((v) => {
            if (!current.written) current.enabled$.value = v
        })
        .catch(errorHandler)
    return current
})
const state$ = globalData(() => state.value.enabled$)
state$.initialize()

/**
 * Observable: `true` when autoplay continuation should be enabled for new
 * queues. Subscribe from the settings toggle so the UI reflects flips.
 */
export const autoplayEnabled$: ObservableValue<boolean> = state$

/** Snapshot: `true` when autoplay is enabled. */
export function isAutoplayEnabled(): boolean {
    return state$.value
}

/** Persist the toggle and update {@link autoplayEnabled$} immediately. */
export function writeAutoplayEnabled(next: boolean): void {
    state.value.written = true
    state$.value = next
    autoplayEnabled.set(next).catch(errorHandler)
}
